/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type Key,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import {
  DismissButton,
  mergeProps,
  Overlay,
  useFocusRing,
  useHover,
  useMenu,
  useMenuItem,
  useMenuSection,
  useMenuTrigger,
  usePopover,
  useSeparator,
  type AriaMenuProps,
  type AriaPopoverProps,
} from "react-aria";
import {
  Item,
  Section,
  useMenuTriggerState,
  useTreeState,
  type Node,
  type OverlayTriggerState,
  type TreeState,
} from "react-stately";

import { cssFns, type Style } from "@superweb/css";
import {
  icons,
  useCustomScrollbar,
  useDebouncedState,
  useIsMobile,
  useTypo,
  useUiColors,
  useUiShadows,
} from "@superweb/ui";
import { useLocale } from "@superweb/intl";

import { UserToolbarButton } from "./button";
import { Search } from "./search";
import { ParkAvatar } from "./park-avatar";
import { Specifications } from "./specifications";

const MENU_MARGIN = 8;
const SUB_MENU_WIDTH = 230;

const MaxHeightContext = createContext<string | number | undefined>(undefined);

type MenuButtonItem = {
  type: "button";
  label: string;
  description?: string;
  icon?: ReactElement;
  onPress: () => void;
};

type CheckboxMenuItem = {
  type: "checkbox";
  label: string;
  description?: string;
  icon?: ReactElement;
  selected?: boolean;
  onPress: () => void;
};

type ParkMenuItem = {
  type: "park";
  id: string;
  label: string;
  description?: string;
  specifications: string[];
  brand: string;
  selected?: boolean;
  onPress: () => void;
};

type MenuButtonSubmenu = {
  type: "submenu";
  label: string;
  description?: string;
  icon?: ReactElement;
  items: (CheckboxMenuItem | false | null | undefined)[];
};

type MenuButtonGroup = {
  type: "group";
  items: (MenuButtonItem | MenuButtonSubmenu | false | null | undefined)[];
};

type CheckboxMenuGroup = {
  type: "checkboxGroup";
  items: (CheckboxMenuItem | false | null | undefined)[];
};

type ParkGroup = {
  type: "parkGroup";
  items: (ParkMenuItem | false | null | undefined)[];
};

export const UserToolbarMenuButton = ({
  label,
  icon,
  description,
  items,
  searchParams,
  onOpenChange,
}: {
  label?: string;
  icon?: ReactNode;
  description?: string;
  items: (
    | MenuButtonGroup
    | CheckboxMenuGroup
    | ParkGroup
    | false
    | null
    | undefined
  )[];
  searchParams?: {
    searchable: boolean;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
  };
  onOpenChange?: (isOpen: boolean) => void;
}) => {
  const state = useMenuTriggerState({ onOpenChange });
  const isMobile = useIsMobile();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const contentRef = useRef(null);
  const { menuTriggerProps, menuProps } = useMenuTrigger<ReactElement>(
    {},
    state,
    buttonRef,
  );
  const [openSubmenuKey, setOpenSubmenuKey] = useState<string>();
  const [isOpenSubMenu, setIsOpenSubMenu] = useState<boolean>(false);

  const onActions = new Map<string, () => void>();

  items
    .filter((item) => !!item)
    .forEach((elem, index) => {
      elem.items
        .filter((item) => !!item)
        .forEach((groupElem, fromGroupIndex) => {
          const itemKey = index + " section: " + fromGroupIndex;

          switch (groupElem.type) {
            case "park":
            case "button":
            case "checkbox": {
              onActions.set(itemKey, () => {
                groupElem.onPress();
                state.close();
              });
              break;
            }
            case "submenu": {
              onActions.set(itemKey, () => {
                setOpenSubmenuKey(itemKey);
                setIsOpenSubMenu((v) =>
                  itemKey !== openSubmenuKey ? true : !v,
                );
              });
              break;
            }
          }
        });
    });

  const onAction = (key: Key) => {
    onActions.get(String(key))?.();
  };

  // Workaround for https://github.com/adobe/react-spectrum/issues/1513#issuecomment-2346989663
  // Issue fixed only onPress event, but not onPointerUp event
  // Corrects the triggering of clicks on elements under the overlay
  // by debouncing the open state
  const debouncedIsOpen = useDebouncedState(state.isOpen, 0);
  // apply debounced state only on mobile in `true` -> `false` phase
  const isOpen = (isMobile && debouncedIsOpen) || state.isOpen;
  // apply debounced state only on mobile in `true` -> `false` phase
  const debouncedIsOpenSubMenu = useDebouncedState(isOpenSubMenu, 0);

  useEffect(() => {
    if (!isOpen) {
      searchParams?.onChange("");
      setIsOpenSubMenu(false);
    }
  }, [isOpen, searchParams]);

  return (
    <>
      <UserToolbarButton
        label={label}
        icon={icon}
        description={description}
        buttonRef={buttonRef}
        externalAriaProps={menuTriggerProps}
      />
      {isOpen && (
        <Popover state={state} triggerRef={buttonRef} placement="bottom start">
          <Menu
            {...menuProps}
            menuRef={menuRef}
            onAction={onAction}
            searchParams={searchParams}
            autoFocus={state.focusStrategy ? state.focusStrategy : false}
            onClose={() => {}}
            selectionMode="none"
          >
            {items
              .filter((item) => !!item)
              .flatMap((item, index) => {
                return (
                  <Section key={index}>
                    {item.items
                      .filter((item) => !!item)
                      .flatMap((sectionItem, itemIndex) => {
                        const itemKey = index + " section: " + itemIndex;

                        switch (sectionItem.type) {
                          case "button": {
                            return (
                              <Item key={itemKey} textValue={sectionItem.label}>
                                <MenuItemContent
                                  contentRef={contentRef}
                                  label={sectionItem.label}
                                  description={sectionItem.description}
                                  icon={sectionItem.icon}
                                />
                              </Item>
                            );
                          }
                          case "checkbox": {
                            return (
                              <Item key={itemKey} textValue={sectionItem.label}>
                                <MenuItemContent
                                  contentRef={contentRef}
                                  label={sectionItem.label}
                                  description={sectionItem.description}
                                  icon={sectionItem.icon}
                                  additionalIcon={
                                    sectionItem.selected ? (
                                      <icons.Check />
                                    ) : undefined
                                  }
                                />
                              </Item>
                            );
                          }
                          case "park": {
                            return (
                              <Item key={itemKey} textValue={sectionItem.label}>
                                <MenuItemContent
                                  contentRef={contentRef}
                                  label={sectionItem.label}
                                  description={sectionItem.description}
                                  specifications={sectionItem.specifications}
                                  brand={sectionItem.brand}
                                  icon={
                                    <ParkAvatar
                                      id={sectionItem.id}
                                      name={sectionItem.label}
                                      size={isMobile ? "m" : "l"}
                                    />
                                  }
                                  additionalIcon={
                                    sectionItem.selected ? (
                                      <icons.Check />
                                    ) : undefined
                                  }
                                  size={isMobile ? "m" : "l"}
                                />
                              </Item>
                            );
                          }
                          case "submenu": {
                            return (
                              <Item key={itemKey} textValue={sectionItem.label}>
                                <MenuItemContentSubmenu
                                  label={sectionItem.label}
                                  description={sectionItem.description}
                                  icon={sectionItem.icon}
                                  items={sectionItem.items}
                                  buttonRef={buttonRef}
                                  menuRef={menuRef}
                                  open={
                                    ((isMobile && debouncedIsOpenSubMenu) ||
                                      isOpenSubMenu) &&
                                    openSubmenuKey === itemKey
                                  }
                                  onClose={() => {
                                    setIsOpenSubMenu(false);
                                    state.close();
                                  }}
                                />
                              </Item>
                            );
                          }
                        }
                      })}
                  </Section>
                );
              })}
          </Menu>
        </Popover>
      )}
    </>
  );
};

const Popover = (
  props: {
    children: ReactNode;
    state: OverlayTriggerState;
  } & Omit<AriaPopoverProps, "popoverRef">,
) => {
  const ref = useRef<HTMLDivElement>(null);
  const { state, children } = props;
  const { popoverProps, underlayProps } = usePopover(
    {
      ...props,
      popoverRef: ref,
    },
    state,
  );
  return (
    <Overlay>
      <div
        {...underlayProps}
        css={{
          position: "fixed",
          ...cssFns.inset("0"),
        }}
      />
      <div
        {...popoverProps}
        ref={ref}
        css={{
          ...cssFns.margin("8px", "0"),
        }}
      >
        <DismissButton onDismiss={state.close} />
        <MaxHeightContext.Provider value={popoverProps.style?.maxHeight}>
          {children}
        </MaxHeightContext.Provider>
        <DismissButton onDismiss={state.close} />
      </div>
    </Overlay>
  );
};

const Menu = (
  props: {
    disabledItems?: Set<Key>;
    menuRef: RefObject<HTMLDivElement>;
    searchParams?: {
      searchable: boolean;
      placeholder: string;
      value: string;
      onChange: (value: string) => void;
    };
  } & AriaMenuProps<ReactElement>,
) => {
  const menuListRef = useRef<HTMLUListElement>(null);
  const state = useTreeState({ ...props });
  const { menuProps } = useMenu(props, state, menuListRef);
  const [width, setWidth] = useState(0);

  const uiColors = useUiColors();
  const uiShadows = useUiShadows();
  const customScrollbarCSS = useCustomScrollbar();

  const maxHeight = useContext(MaxHeightContext);

  // Workaround for https://github.com/adobe/react-spectrum/issues/1513
  // Corrects the triggering of clicks on elements under the overlay
  useEffect(() => {
    const handler = (event: TouchEvent) => {
      const closestMenuItem = (event.target as HTMLElement).closest(
        "[role=menuitem]",
      );
      if (
        event.cancelable &&
        typeof closestMenuItem?.getAttribute("href") !== "string"
      ) {
        event.preventDefault();
      }
    };

    const menuElement = menuListRef.current;
    menuElement?.addEventListener("touchend", handler);

    return () => {
      menuElement?.removeEventListener("touchend", handler);
    };
  }, [menuListRef]);

  useLayoutEffect(() => {
    if (props.menuRef.current) {
      setWidth(props.menuRef.current.offsetWidth);
    }
  }, [props.menuRef]);

  return (
    <div
      ref={props.menuRef}
      css={{
        minWidth: "160px",
        maxWidth: "350px",
        maxHeight: maxHeight + "px",
        backgroundColor: uiColors.background,
        boxShadow: uiShadows.bottomNormal,
        ...cssFns.border({
          radius: "10px",
        }),
        overflowY: "auto",
        ...(width > 0 ? { width: width + "px" } : {}),
      }}
      {...customScrollbarCSS}
    >
      {props.searchParams?.searchable && (
        <div
          css={{
            display: "grid",
            ...cssFns.padding("8px"),
          }}
        >
          <Search
            placeholder={props.searchParams.placeholder}
            value={props.searchParams.value}
            onChange={(value) => {
              props.searchParams?.onChange(value);
            }}
          />
        </div>
      )}

      <ul
        {...menuProps}
        ref={menuListRef}
        css={{
          ...cssFns.margin("0"),
          ...cssFns.padding("0"),
          listStyleType: "none",
        }}
      >
        {[...state.collection].map((item, index) => {
          if (item.type === "section") {
            return (
              <MenuItemSection
                key={item.key}
                section={item}
                state={state}
                sectionIndex={
                  index === [...state.collection].length - 1
                    ? "last"
                    : undefined
                }
              />
            );
          }
          return (
            <MenuItem
              key={item.key}
              item={item}
              state={state}
              radiusTop={index === 0}
              radiusBottom={index === [...state.collection].length - 1}
            />
          );
        })}
      </ul>
    </div>
  );
};

const MenuItemSection = ({
  section,
  state,
  sectionIndex,
}: {
  section: Node<ReactElement>;
  state: TreeState<ReactElement>;
  sectionIndex: "first" | "last" | undefined;
}) => {
  const { itemProps, groupProps } = useMenuSection({
    heading: section.rendered,
    "aria-label": section["aria-label"],
  });
  const { separatorProps } = useSeparator({
    elementType: "li",
  });
  const uiColors = useUiColors();
  return (
    <>
      {section.key !== state.collection.getFirstKey() && (
        <li
          {...separatorProps}
          css={{
            ...cssFns.border({
              width: "0.5px",
              style: "solid",
              color: uiColors.line,
            }),
            opacity: "0.5",
          }}
        />
      )}
      <li {...itemProps}>
        <ul {...groupProps} css={{ listStyleType: "none", paddingLeft: "0" }}>
          {[...section.props.children].map((item, index) => (
            <MenuItem
              key={item.key}
              item={item}
              state={state}
              radiusTop={
                !section.props.title && sectionIndex === "first" && index === 0
              }
              radiusBottom={
                sectionIndex === "last" &&
                index === [...section.props.children].length - 1
              }
            />
          ))}
        </ul>
      </li>
    </>
  );
};

const MenuItem = ({
  item,
  state,
  radiusTop = false,
  radiusBottom = false,
}: {
  item: Node<ReactElement>;
  state: TreeState<ReactElement>;
  radiusTop?: boolean;
  radiusBottom?: boolean;
}) => {
  const ref = useRef(null);
  const { menuItemProps, isPressed } = useMenuItem(
    {
      key: item.key,
    },
    state,
    ref,
  );
  const { hoverProps, isHovered } = useHover({});

  const { isFocusVisible, focusProps } = useFocusRing();

  const uiColors = useUiColors();
  const backgroundColor =
    isPressed || isHovered || isFocusVisible
      ? isPressed
        ? uiColors.press
        : uiColors.hover
      : uiColors.background;

  const liStyle: Style = {
    backgroundColor: backgroundColor,
    outlineStyle: "none",
    cursor: "pointer",
    borderTopLeftRadius: radiusTop ? "10px" : "0px",
    borderTopRightRadius: radiusTop ? "10px" : "0px",
    borderBottomLeftRadius: radiusBottom ? "10px" : "0px",
    borderBottomRightRadius: radiusBottom ? "10px" : "0px",
    transitionDuration: "0.3s",
    color: uiColors.text,
  };

  return (
    <li
      {...mergeProps(menuItemProps, hoverProps, focusProps)}
      ref={ref}
      css={liStyle}
    >
      {item.rendered || item.props.children}
    </li>
  );
};

const MenuItemContentSubmenu = ({
  label,
  description,
  icon,
  items,
  buttonRef,
  menuRef,
  open,
  onClose,
}: {
  label: string;
  description?: string;
  icon?: ReactElement;
  items: (MenuButtonItem | CheckboxMenuItem | false | null | undefined)[];
  buttonRef: RefObject<HTMLElement>;
  menuRef: RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
}) => {
  const uiColors = useUiColors();
  const { textInfo } = useLocale();
  const isMobile = useIsMobile();

  const contentRef = useRef<HTMLDivElement>(null);

  const [positionCss, setPositionCss] = useState<Style>({});

  useLayoutEffect(() => {
    if (!open) {
      setPositionCss({});
      return;
    }

    const contentElement = contentRef.current;
    const buttonElement = buttonRef.current;
    const menuElement = menuRef.current;

    if (!contentElement || !buttonElement || !menuElement) {
      setPositionCss({});
      return;
    }

    const css: Style = {};

    // Horizontal orientation
    const triggerWidth = Number(contentElement.clientWidth),
      triggerRight = contentElement.getBoundingClientRect().right,
      documentWidth = document.documentElement.clientWidth;

    if (documentWidth - triggerRight > SUB_MENU_WIDTH) {
      // There is enough space to the right of the menu => Submenu on right
      css["left"] = isMobile
        ? textInfo.direction === "rtl"
          ? "0px"
          : "50px"
        : `${triggerWidth + MENU_MARGIN}px`;
    } else {
      // There is not enough space to the right of the menu => Submenu on left
      css["left"] = isMobile
        ? textInfo.direction === "rtl"
          ? "0px"
          : "-50px"
        : `-${SUB_MENU_WIDTH + MENU_MARGIN}px`;
    }

    // Vertical orientation
    const triggerHeight = Number(contentElement.clientHeight),
      subMenuHeight = items.length * triggerHeight;

    if (
      buttonElement.getBoundingClientRect().top <
      contentElement.getBoundingClientRect().top
    ) {
      const shift = contentElement.offsetTop;
      const scrollPosition = menuElement.scrollTop;
      let topPosition =
        shift - subMenuHeight / 2 + triggerHeight / 2 - scrollPosition;

      const menuRect = menuElement.getBoundingClientRect();
      const offScreen =
        menuRect.top + topPosition + subMenuHeight - window.innerHeight;
      if (offScreen > 0) {
        topPosition = topPosition - offScreen;
      }

      css["top"] = `${topPosition > 0 ? topPosition : 0}px`;
    } else {
      // Menu at the top of the button

      if (isMobile) {
        css["bottom"] = "0";
      } else {
        const shift =
          buttonElement.getBoundingClientRect().top -
          contentElement.getBoundingClientRect().bottom -
          MENU_MARGIN;
        const bottomPosition = shift - subMenuHeight / 2 + triggerHeight / 2;

        css["bottom"] = `${bottomPosition > 0 ? bottomPosition : 0}px`;
      }
    }

    setPositionCss(css);
  }, [open, items.length, isMobile, buttonRef, menuRef, textInfo.direction]);

  return (
    <>
      <MenuItemContent
        label={label}
        description={description}
        icon={icon}
        additionalIcon={
          <div css={{ color: uiColors.textMinor }}>
            {textInfo.direction === "rtl" ? (
              <icons.ChevronLeft />
            ) : (
              <icons.ChevronRight />
            )}
          </div>
        }
        contentRef={contentRef}
      />
      {open && (
        <div
          css={{
            position: "absolute",
            ...positionCss,
          }}
        >
          <SubMenu items={items} onClick={onClose} />
        </div>
      )}
    </>
  );
};

const SubMenu = ({
  items,
  onClick,
}: {
  items: (MenuButtonItem | CheckboxMenuItem | false | null | undefined)[];
  onClick: () => void;
}) => {
  const uiColors = useUiColors();
  const uiShadows = useUiShadows();
  const customScrollbarCSS = useCustomScrollbar();

  const maxHeight = useContext(MaxHeightContext);

  return (
    <ul
      css={{
        width: `${SUB_MENU_WIDTH}px`,
        ...cssFns.padding("0"),
        listStyleType: "none",
        backgroundColor: uiColors.background,
        boxShadow: uiShadows.bottomNormal,
        ...cssFns.border({
          radius: "10px",
        }),
        overflowY: "auto",
        maxHeight: maxHeight + "px",
      }}
      {...customScrollbarCSS}
    >
      {items.flatMap((item, index) => {
        if (!item) return;
        return <SubMenuItem key={index} item={item} onClick={onClick} />;
      })}
    </ul>
  );
};

const SubMenuItem = ({
  item,
  onClick,
}: {
  item: MenuButtonItem | CheckboxMenuItem;
  onClick: () => void;
}) => {
  const contentRef = useRef(null);

  const { hoverProps, isHovered } = useHover({});
  const { isFocusVisible, focusProps } = useFocusRing();

  const uiColors = useUiColors();
  const backgroundColor =
    isHovered || isFocusVisible
      ? cssFns.blend(
          cssFns.transparentize(uiColors.background, 1),
          cssFns.transparentize(uiColors.everBack, 0.9),
        )
      : cssFns.transparentize(uiColors.background, 1);

  return (
    <li
      role="menuitem"
      {...hoverProps}
      {...focusProps}
      css={{
        backgroundColor: backgroundColor,
        outlineStyle: "none",
        cursor: "pointer",
        transitionDuration: "0.3s",
        color: uiColors.text,
      }}
      onPointerUp={() => {
        onClick();
        item.onPress();
      }}
    >
      <MenuItemContent
        contentRef={contentRef}
        label={item.label}
        description={item.description}
        icon={item.icon}
        additionalIcon={
          item.type === "checkbox" && item.selected ? (
            <icons.Check />
          ) : undefined
        }
      />
    </li>
  );
};

const MenuItemContent = ({
  label,
  description,
  icon,
  additionalIcon,
  contentRef,
  specifications,
  brand,
  size = "s",
}: {
  label?: string;
  description?: string;
  icon?: ReactElement;
  additionalIcon?: ReactNode;
  contentRef?: RefObject<HTMLDivElement>;
  specifications?: string[];
  brand?: string;
  size?: "s" | "m" | "l";
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const isMobile = useIsMobile();

  const ref = useRef<HTMLDivElement>(null);
  const internalContentRef = contentRef || ref;

  return (
    <div
      ref={internalContentRef}
      css={{
        display: "grid",
        alignContent: "center",
        justifyContent: "space-between",
        alignItems: "center",
        ...cssFns.padding("0px", "16px"),
        gridTemplateRows: "16px [content] auto 16px",
        backgroundColor: "transparent",
        height: size === "s" ? "40px" : size === "m" ? "58px" : "72px",
        ...(icon && { paddingInlineStart: "10px" }),
        ...(additionalIcon && {
          paddingInlineEnd: "10px",
        }),
      }}
    >
      <div
        css={{
          display: "grid",
          alignItems: "center",
          justifyContent: "start",
          gridRowStart: "content",
          gridTemplateRows: "[subcontent] auto",
        }}
      >
        {icon && (
          <div
            css={{
              minWidth: "28px",
              minHeight: "28px",
              display: "grid",
              alignItems: "center",
              justifyContent: "start",
              gridRowStart: "subcontent",
              paddingInlineEnd: "6px",
              color: uiColors.textMinor,
            }}
          >
            {icon}
          </div>
        )}
        {label && (
          <span
            css={{
              ...typo({
                level: "caption1",
                weight: "regular",
                density: "tight",
              }),
              ...cssFns.overflow("hidden"),
              gridRowStart: "subcontent",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {label}
            {description && (
              <span
                css={{
                  ...cssFns.overflow("hidden"),
                  gridRowStart: "subcontent",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: uiColors.textMinor,
                  ...typo({
                    level: "caption2",
                    weight: "regular",
                    density: "tight",
                  }),
                  display: "block",
                }}
              >
                {description}
              </span>
            )}
            {specifications && brand && (
              <Specifications
                specifications={specifications}
                brand={brand}
                size={isMobile ? "s" : "m"}
              />
            )}
          </span>
        )}
      </div>
      {additionalIcon ? (
        <div
          css={{
            gridRowStart: "content",
            width: "24px",
            height: "24px",
            paddingInlineStart: "10px",
          }}
        >
          {additionalIcon}
        </div>
      ) : null}
    </div>
  );
};
